/** Where the user is, as far as the sidebar cares: the path and its `?query`. */
export interface NavLocation {
    readonly pathname: string;
    readonly search: string;
}

/** Each required `?key=value` adds one; path length always outranks query params. */
const PATH_WEIGHT = 100;

/** True when `pathname` is `url` itself or a route nested under it. */
export function matchesNavPath(pathname: string, url: string | undefined): boolean {
    return !!url && (pathname === url || pathname.startsWith(url + '/'));
}

function splitNavUrl(url: string): { path: string; query: URLSearchParams } {
    const at = url.indexOf('?');
    if (at < 0) return { path: url, query: new URLSearchParams() };
    return { path: url.slice(0, at), query: new URLSearchParams(url.slice(at + 1)) };
}

/** Every `key=value` the nav url requires is present in the current `search`. */
function hasRequiredParams(search: string, query: URLSearchParams): boolean {
    const current = new URLSearchParams(search);
    let isMatch = true;
    query.forEach((value, key) => {
        if (current.get(key) !== value) isMatch = false;
    });
    return isMatch;
}

function countParams(query: URLSearchParams): number {
    let count = 0;
    query.forEach(() => {
        count += 1;
    });
    return count;
}

/**
 * How specifically a nav `url` claims `location`, or -1 when it claims none.
 *
 * Longer path wins (`/opportunities/reports` over `/opportunities`); on the same
 * path, a url requiring more `?key=value` pairs wins — so on `/opportunities?mine=1`
 * "My deals" outranks "All deals". Extra params in the location (`&pipeline=…`)
 * are ignored. A query url owns only its own page, never the detail routes under
 * it, so `/opportunities/:id` stays with "All deals". The prefix branch is what
 * keeps a row lit on a detail page no row claims (`/contacts` on `/contacts/123`).
 */
export function navMatchScore(location: NavLocation, url: string | undefined): number {
    if (!url || url === '#') return -1;
    const { path, query } = splitNavUrl(url);
    const params = countParams(query);
    const isExact = location.pathname === path;
    const isNested = path !== '/' && location.pathname.startsWith(path + '/');
    if (!isExact && !(isNested && params === 0)) return -1;
    if (!hasRequiredParams(location.search, query)) return -1;
    return path.length * PATH_WEIGHT + params;
}

/**
 * The one sub-item `url` in a group that should read as active: the most
 * specific match (see {@link navMatchScore}). On `/opportunities/reports` that is
 * "Deal reports", not also "All deals"; on `/opportunities?mine=1` it is
 * "My deals"; on `/opportunities/:id` only "All deals" matches.
 */
export function activeNavUrl(
    items: readonly { url?: string }[] | undefined,
    location: NavLocation,
): string | undefined {
    let best: string | undefined;
    let bestScore = -1;
    for (const { url } of items ?? []) {
        const score = navMatchScore(location, url);
        if (score > bestScore) {
            best = url;
            bestScore = score;
        }
    }
    return best;
}
